import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  AccessRequestsApiListAccessRequestsRequest,
  PaginatedClientResponseModelAccessRequestClientModel,
  useListAccessRequests,
  API_LIMIT,
} from '@apono/client-app-sdk';

import { PAGE_PARAM, REQUEST_PARAM } from '@common/constants';

export default function useRequestsTable({
  statuses,
  scope,
  refetch,
}: {
  statuses: AccessRequestsApiListAccessRequestsRequest['statuses'];
  scope: AccessRequestsApiListAccessRequestsRequest['scope'];
  refetch?: number;
}) {
  const [queryParams, setQueryParams] = useSearchParams();
  const [cachedPageData, setCachedPageData] = useState<
    PaginatedClientResponseModelAccessRequestClientModel | undefined
  >();

  const pageParam = useMemo(() => {
    const pageQp = queryParams.get(PAGE_PARAM);
    const page = pageQp ? parseInt(pageQp) : 1;
    return (page - 1) * API_LIMIT;
  }, [queryParams]);

  const { data, fetchNextPage, isFetchingNextPage, isFetched } = useListAccessRequests({
    limit: API_LIMIT,
    skip: pageParam,
    statuses,
    scope,
    refetch,
  });

  const reactQueryPageData = useMemo(() => {
    if (!data) return undefined;

    const pageParamIndex = data.pageParams.findIndex((p) => p === pageParam);
    if (pageParamIndex === -1) return undefined;

    return data.pages[pageParamIndex];
  }, [data, pageParam]);

  useEffect(() => {
    if (!reactQueryPageData && pageParam > 0) {
      fetchNextPage({ pageParam });
      return;
    }

    setCachedPageData(reactQueryPageData);
  }, [fetchNextPage, pageParam, reactQueryPageData]);

  const selectedRequestId = useMemo(() => queryParams.get(REQUEST_PARAM) || undefined, [queryParams]);

  const handleRequestClick = useCallback(
    (requestId: string) => {
      queryParams.set(REQUEST_PARAM, requestId);
      setQueryParams(queryParams);
    },
    [queryParams, setQueryParams],
  );

  const handleOnPageChange = useCallback(
    (newPage: number) => {
      queryParams.set(PAGE_PARAM, String(newPage));
      setQueryParams(queryParams);
    },
    [queryParams, setQueryParams],
  );

  return {
    isFetched,
    isLoading: isFetchingNextPage,
    pageData: reactQueryPageData || cachedPageData,
    selectedRequestId,
    handleRequestClick,
    handleOnPageChange,
  };
}
